<template>
  <b-container fluid>
    <b-row>
      <b-col>
        <reports-filter
          is-company-field-required
          is-installation-field-required
          is-asset-field-required
          :filter.sync="reportsFilter"
          :filter-objects.sync="reportsFilterObjects"
          @update:filter="onUpdateReportsFilter"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col
        xs="12"
        md="6"
      >
        <tag-options-input
          class="mb-3"
          is-required-field
          :label="$t('reports.diagnostics-history.filter.module')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="modules"
          :selected-items.sync="selectedModules"
          :options-custom-label="$t('reports.filters.options.all-modules')"
          :disabled="isModulesDisabled"
          @update:selectedItems="onUpdateModules"
        />
      </b-col>
      <b-col
        xs="12"
        md="6"
      >
        <tag-options-input
          class="mb-3"
          is-required-field
          :label="$t('reports.diagnostics-history.filter.norm')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="norms"
          :selected-items.sync="selectedNorms"
          :options-custom-label="$t('reports.filters.options.all-norms')"
          :disabled="isNormsDisabled"
          @update:selectedItems="onUpdateNorms"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col
        xs="12"
        md="6"
      >
        <tag-options-input
          class="mb-3"
          :is-required-field="modulesHasChromatography"
          :label="$t('reports.diagnostics-history.filter.method')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="methods"
          :selected-items.sync="selectedMethods"
          :options-custom-label="$t('reports.filters.options.all-methods')"
          :disabled="isMethodsDisabled"
          :loading="loadingMethods"
          @update:selectedItems="onUpdateMethods"
        />
      </b-col>
      <b-col
        xs="12"
        md="6"
      >
        <tag-options-input
          class="mb-4"
          is-required-field
          :label="$t('reports.diagnostics-history.filter.classification')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="diagnosticClassifications"
          :selected-items.sync="selectedDiagnosticClassifications"
          :options-custom-label="$t('reports.filters.options.all-diagnostic-classifications')"
          :disabled="isDiagnosticClassificationsDisabled"
          @update:selectedItems="onUpdateDiagnosticClassifications"
        />
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col>
        <diagnostic-list
          :chromatography="chromatography"
          :physical-chemical="physicalChemical"
          :loading="loadingDiagnostics"
          :selected-chromatography-diagnostics.sync="selectedChromatographyDiagnostics"
          :selected-physical-chemical-diagnostics.sync="selectedPhysicalChemicalDiagnostics"
        />
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col>
        <period-date-select
          class="diagnostics-history__period-select"
          :initialDate.sync="reportsFilter.initialDate"
          :initialTime.sync="reportsFilter.initialTime"
          :finalDate.sync="reportsFilter.finalDate"
          :finalTime.sync="reportsFilter.finalTime"
          :period.sync="reportsFilter.period"
          :time-zone="timeZoneUTC"
          @update:initialDate="validatePeriodFields($event, 'initialDate')"
          @update:initialTime="validatePeriodFields($event, 'initialTime')"
          @update:finalDate="validatePeriodFields($event, 'finalDate')"
          @update:finalTime="validatePeriodFields($event, 'finalTime')"
          @update:initialDateTime="onUpdateInitialDateTime($event)"
          @update:finalDateTime="onUpdateFinalDateTime($event)"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <additional-fields
          :tabs-to-show="assetTypesByFilter"
          @fields-selected="setAdditionalFields"
        />
      </b-col>
      <b-col align-h="end">
        <div class="diagnostics-history__buttons">
          <b-button
            variant="outline-green"
            :disabled="disableExport"
            @click="validateAndGenerateReport(exportXlsx)"
          >
            <s-loader
              v-if="loading"
              class="diagnostics-history__xlsx-loading"
            />
            <i
              v-else
              aria-hidden="true"
              class="fas fa-file-excel mr-1"
            />
            <span>{{ $t('general.export-xlsx') }}</span>
          </b-button>
          <b-button
            class="ml-2"
            variant="primary"
            :disabled="!isPeriodValid"
            @click="validateAndGenerateReport(openReport)"
          >
            {{ $t('reports.filters.view-report') }}
          </b-button>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import uniq from 'lodash/uniq';
import PeriodDateSelect from '@/components/PeriodDateSelect';
import ReportsFilter from '@/components/ReportsFilter';
import TagOptionsInput from '@/components/inputs/TagOptionsInput';
import AdditionalFields from '@/domain/components/generalPanel/reports/additionalFields/AdditionalFields';
import StorageManager from '@/services/sessionStorageManager';
import Service from '@/domain/services/generalPanel/reports/diagnostics';
import ModulesService from '@/domain/services/installation/engineering/modules';
import { grammaticalJoin } from '@/services/arrayUtils';
import LoadingMixin from '@/services/loadingMixin';
import GenerateReportMixin from '@/domain/mixins/generalPanel/reports/generateReportMixin';
import { Rules, Modules } from 'sigma-mappings';
import periods from '@/domain/enums/periods';
import {
  chromatographyNormsDictionary,
  physicalChemicalNormsDictionary,
} from '@/domain/data/generalPanel/reports/diagnosticsHistory/normsDictionary';
import {
  methodDUVAL,
  methodIEC60599,
  methodIEEEC57104,
  methodNBR7274,
} from '@/domain/data/generalPanel/reports/diagnosticsHistory/methodsDictionary';
import modulesDictionary from '@/domain/data/generalPanel/reports/diagnosticsHistory/modulesDictionary';
import diagnosticsClassificationsDictionary
  from '@/domain/data/generalPanel/reports/diagnosticsHistory/diagnosticsClassificationsDictionary';
import DiagnosticList from '@/domain/components/generalPanel/reports/diagnosticsHistory/DiagnosticList';
import { timezones } from '@/constants/localizer';

export default {
  components: {
    DiagnosticList,
    TagOptionsInput,
    PeriodDateSelect,
    ReportsFilter,
    AdditionalFields,
  },
  mixins: [
    LoadingMixin,
    GenerateReportMixin,
  ],
  data: () => ({
    diagnostics: null,
    chromatography: null,
    physicalChemical: null,
    reportsFilter: {
      period: periods.SEVEN,
    },
    reportsFilterObjects: {},
    requiredFields: [],
    emptyFields: [],
    modules: [],
    selectedModules: [],
    norms: [],
    selectedNorms: [],
    methods: [],
    selectedChromatographyDiagnostics: [],
    selectedPhysicalChemicalDiagnostics: [],
    selectedMethods: [],
    loadingMethods: false,
    loadingDiagnostics: false,
    diagnosticClassifications: [],
    selectedDiagnosticClassifications: [],
    timeZoneUTC: timezones.UTC,
  }),
  computed: {
    assetTypesByFilter() {
      const assets = this.reportsFilterObjects.assets || [];
      return assets.map(asset => (asset.assetType || {}).name);
    },
    selectedDiagnostics() {
      return [
        ...this.selectedChromatographyDiagnostics,
        ...this.selectedPhysicalChemicalDiagnostics,
      ];
    },
    disableExport() {
      return !this.isPeriodValid || this.loading;
    },
    isPeriodsUndefined() {
      return (this.reportsFilter.period === periods.PERSONALIZED
        || this.reportsFilter.period === undefined);
    },
    isDateFilled() {
      const {
        initialDate,
        finalDate,
        initialTime,
        finalTime,
      } = this.reportsFilter;
      return initialDate && finalDate && initialTime && finalTime;
    },
    isPeriodValid() {
      return !this.reportsFilter.invalid
        && (this.isDateFilled || this.reportsFilter.period !== periods.PERSONALIZED);
    },
    isModulesDisabled() {
      const { assetsIds } = this.reportsFilter;
      return !(assetsIds || []).length;
    },
    isNormsDisabled() {
      return !(this.selectedModules || []).length;
    },
    hasSomeChromatographyNormSelected() {
      const chromatographyNorms = chromatographyNormsDictionary(this);
      if (!this.selectedNorms) {
        return false;
      }
      const selectedNormsIds = this.selectedNorms.map(it => it.id);
      const chromatographyNormsIds = chromatographyNorms.map(it => it.id);
      return selectedNormsIds.some(it => chromatographyNormsIds.includes(it));
    },
    isMethodsDisabled() {
      if (!this.modulesHasChromatography || !this.hasSomeChromatographyNormSelected) {
        return true;
      }
      return !(this.selectedNorms || []).length;
    },
    isDiagnosticClassificationsDisabled() {
      if (!(this.selectedNorms || []).length) {
        return true;
      }
      return !this.selectedMethods.length && this.modulesHasChromatography;
    },
    modulesHasChromatography() {
      if (!this.selectedModules || !this.selectedModules.length) {
        return false;
      }
      return this.selectedModules
        .some(item => item.id === Modules.CHROMATOGRAPHY);
    },
    modulesHasPhysicalChemical() {
      return (this.selectedModules || [])
        .some(item => item.id === Modules.PHYSICAL_CHEMICAL);
    },
    methodsByNorms() {
      return {
        [Rules.IEC_60599]: methodIEC60599(this),
        [Rules.IEEE_C57_104]: methodIEEEC57104(this),
        [Rules.NBR_7274]: methodNBR7274(this),
        [Rules.DUVAL]: methodDUVAL(this),
      };
    },
  },
  created() {
    this.defineDiagnosticClassifications();
    this.getRequiredFields();
  },
  methods: {
    getDiagnostics() {
      const {
        modules,
        norms,
        classifications,
        methods,
      } = this.reportsFilter;
      const payload = {
        modules,
        norms,
        classifications,
        methods,
      };
      this.loadingDiagnostics = true;
      Service.getDiagnostics(payload)
        .then((data) => {
          this.mapPhysicalChemical(data);
          this.mapChromatography(data);
          this.diagnostics = data;
      })
      .finally(() => {
        this.loadingDiagnostics = false;
      });
    },
    mapPhysicalChemical(data) {
      const [physicalChemicalProxy] = data.filter(it => it.module === 'physical-chemical');
      if (!physicalChemicalProxy) {
        this.physicalChemical = null;
        return;
      }

      this.physicalChemical = physicalChemicalProxy;
    },
    mapChromatography(data) {
      const [chromatographyProxy] = data.filter(it => it.module === 'chromatography');
      if (!chromatographyProxy) {
        this.chromatography = null;
        return;
      }

      this.chromatography = chromatographyProxy;
    },
    getRequiredFields() {
      const fields = [
        'companiesIds',
        'installationsIds',
        'assetsIds',
        'modules',
        'norms',
        'diagnosticsIds',
      ];

      if (this.modulesHasChromatography) {
        fields.push('methods');
      }

      fields.push('classifications');

      this.requiredFields = fields;
    },
    showPeriodValidateMessage() {
      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: grammaticalJoin(this.emptyFields.map(
            field => this.getFieldLabel(field),
          )),
        },
      });
    },
    validatePeriodFields(value, field) {
      if (value) {
        this.emptyFields = this.emptyFields.filter(item => item !== field);
      }
      if ((this.reportsFilter.period !== undefined && this.isPeriodsUndefined)
        && !value && !this.emptyFields.includes(field)) {
        this.emptyFields.push(field);
        this.emptyFields = uniq(this.emptyFields);
        this.showPeriodValidateMessage();
      }
    },
    getFieldLabel(field) {
      const fields = {
        companiesIds: this.$t('entities.company'),
        installationsIds: this.$t('entities.installation'),
        assetsIds: this.$t('entities.asset'),
        modules: this.$t('reports.diagnostics-history.filter.module'),
        norms: this.$t('reports.diagnostics-history.filter.norm'),
        methods: this.$t('reports.diagnostics-history.filter.method'),
        classifications: this.$t('reports.diagnostics-history.filter.classification'),
        initialDate: this.$t('fields.general.initial-date'),
        diagnosticsIds: this.$t('reports.diagnostics-history.filter.diagnostics'),
        finalDate: this.$t('fields.general.final-date'),
        initialTime: this.$t('fields.general.initial-time'),
        finalTime: this.$t('fields.general.final-time'),
      };
      return fields[field];
    },
    setAdditionalFields(value) {
      this.reportsFilter.additionalFields = value;
    },
    validateAndGenerateReport(promise) {
      this.setDiagnosticsToFilter();
      this.generateReport(promise);
    },
    setDiagnosticsToFilter() {
      this.reportsFilter.diagnosticsIds = this.selectedDiagnostics;
    },
    exportXlsx() {
      this.startLoading();
      Service.exportXlsx(this.reportsFilter)
        .then(() => this.$pushAlert({ code: 'MS011' }))
        .finally(() => this.endLoading());
    },
    openReport() {
      const stringifyFilter = JSON.stringify(this.reportsFilter);
      const storageKey = StorageManager.setItemWithId('diagnosticsHistoryFilter', stringifyFilter);
      const routeData = this.$router.resolve({
        name: 'clean-view.diagnostics-history.view-report',
        query: {
          filter: storageKey,
        },
      });
      window.open(routeData.href, '_blank');
    },
    defineModules() {
      this.module = [];
      this.onUpdateModules();
    },
    defineNorms() {
      this.norms = [];
      this.updateNorms();
    },
    defineMethods() {
      this.methods = [];
      this.updateMethods();
    },
    defineDiagnosticClassifications() {
      this.selectedDiagnosticClassifications = [];
      this.onUpdateDiagnosticClassifications();

      this.diagnosticClassifications = diagnosticsClassificationsDictionary(this);
    },
    onUpdateModules() {
      if (!(this.selectedModules || []).length) {
        this.onRemoveAllModules();
      }
      this.reportsFilter.modules = (this.selectedModules || [])
        .map(item => item.id);
      this.getRequiredFields();
      this.updateSelectedDiagnostics();
      this.$nextTick(() => {
        this.updateNorms();
      });
    },
    updateSelectedDiagnostics() {
      if (!this.modulesHasChromatography) {
        this.selectedChromatographyDiagnostics = [];
      }
      if (!this.modulesHasPhysicalChemical) {
        this.selectedPhysicalChemicalDiagnostics = [];
      }
    },
    onUpdateNorms() {
      if (!(this.selectedNorms || []).length) {
        this.onRemoveAllNorms();
      }

      this.reportsFilter.norms = (this.selectedNorms || [])
        .map(item => item.id);
      this.$nextTick(() => {
        this.updateMethods();
      });
    },
    onUpdateMethods() {
      this.reportsFilter.methods = this.selectedMethods
        .map(item => item.id);
      if (this.isDiagnosticClassificationsDisabled) {
        this.reportsFilter.classifications = [];
        this.selectedDiagnosticClassifications = [];
      }
      if (this.selectedDiagnosticClassifications.length) {
        this.getDiagnostics();
      }
    },
    onUpdateDiagnosticClassifications() {
      this.reportsFilter.classifications = this.selectedDiagnosticClassifications
        .map(item => item.id);
      if (this.selectedDiagnosticClassifications.length) {
        this.getDiagnostics();
      }
    },
    onUpdateReportsFilter() {
      const {
        installationsIds,
        assetsIds,
      } = this.reportsFilter;

      if (!(installationsIds || []).length) {
        this.onRemoveAllInstallations();
      }

      if (!(assetsIds || []).length) {
        this.onRemoveAllAssets();
      }

      if ((assetsIds || []).length) {
        this.getModules();
      }
    },
    onUpdateInitialDateTime(value) {
      this.reportsFilter.initialDateTime = value;
    },
    onUpdateFinalDateTime(value) {
      this.reportsFilter.finalDateTime = value;
    },
    onRemoveAllModules() {
      this.selectedNorms = [];
      this.onUpdateNorms();
      this.selectedMethods = [];
      this.onUpdateMethods();
    },
    onRemoveAllNorms() {
      this.selectedMethods = [];
      this.onUpdateMethods();
    },
    onRemoveAllInstallations() {
      this.selectedDiagnosticClassifications = [];
      this.onUpdateDiagnosticClassifications();
    },
    onRemoveAllAssets() {
      this.selectedModules = [];
      this.onUpdateModules();

      this.selectedNorms = [];
      this.onUpdateNorms();

      this.selectedMethods = [];
      this.onUpdateMethods();
    },
    mapModulesDictionary(module) {
      return modulesDictionary(this).filter(item => item.id === module);
    },
    getModules() {
      const { assetsIds } = this.reportsFilter;

      if (!(assetsIds || []).length) {
        return;
      }

      this.loadingMethods = true;

      const params = {
        assetsIds,
      };

      ModulesService.getEngineeringModules(params)
        .then((response) => {
          this.modules = response
            .map(module => this.mapModulesDictionary(module))
            .flat(1);
        })
        .finally(() => { this.loadingMethods = false; });
    },
    filterSelectedNormsBasedOnAvailableNorms() {
      this.selectedNorms = this.selectedNorms.filter(
        selectedNorm => this.norms.some(norm => norm.id === selectedNorm.id),
      );
    },
    getNormsBySelectedModules() {
      const normsList = [];
      this.selectedModules.forEach((module) => {
        if (module.id === Modules.CHROMATOGRAPHY) {
          normsList.push(...chromatographyNormsDictionary(this));
          return;
        }
        normsList.push(...physicalChemicalNormsDictionary(this));
      });
      return normsList;
    },
    updateNorms() {
      this.norms = this.getNormsBySelectedModules();
      this.norms.sort((a, b) => a.name.localeCompare(b.name));
      this.filterSelectedNormsBasedOnAvailableNorms();
      this.onUpdateNorms();
    },
    filterSelectedMethodsBasedOnAvailableMethods() {
      this.selectedMethods = this.selectedMethods.filter(
        selectedMethod => this.methods.some(method => method.id === selectedMethod.id),
      );
    },
    getMethodsBySelectedNorms() {
      const methods = [];
      this.selectedNorms.forEach((norm) => {
        const method = this.methodsByNorms[norm.id];
        if (!method) return;
        methods.push(...method);
      });
      return methods;
    },
    updateMethods() {
      this.methods = this.getMethodsBySelectedNorms();
      this.methods.sort((a, b) => a.name.localeCompare(b.name));
      this.filterSelectedMethodsBasedOnAvailableMethods();
      this.onUpdateMethods();
    },
  },
  watch: {
    selectedDiagnosticClassifications(newValue) {
      if (!newValue.length) {
        this.chromatography = {};
        this.physicalChemical = {};
      }
    },
  },
};
</script>

<style scoped lang="sass">
  .diagnostics-history
    &__period-select
      margin-top: 15px
    &__buttons
      display: flex
      justify-content: flex-end
    &__xlsx-loading
      display: inline-flex
</style>
